import React from 'react';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { IonCol, IonGrid, IonIcon, IonRow, useIonModal } from '@ionic/react';
import { stringToByteArray } from '@ssg/common/Helpers/inputFileHelper';
import { ESDHFileFragment } from '@ssg/common/GraphQL';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import { checkFilesExist, tryGetFile } from './checkFilesExist';
import { ApiMutationKey, useCreateCaseOrganizationReport } from 'Store';
import { GetMobileV2DrivingSlip_drivingSlip_case, GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers } from 'GQL';
import BasePage from 'Components/Layout/BasePage';
import SignatureInput from './Inputs/SignatureInput';
import useDrivingSlipImages from './useDrivingSlipImages';
import Button from 'Components/Button';
import Datepicker from '@ssg/common/Components/Datepicker';
import Input from '@ssg/common/Components/Input';
import RemoveWorkerModal, { CaseOrganizationWorkerModalData, Props as RemoveWorkerModalProps } from './RemoveWorkerModal';
import { CaseOrganizationWorkerInput } from '@ssg/common/GraphQL/indexV2';

const EMPTY_ROW = (index: number): GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers => ({
	index,
	title: '',
	name: '',
	phonenumber: '',
	instructor: '',
	date: '',
	signature: null,
});
const TABLE_COLUMNS: Array<keyof GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers> = [
	'title',
	'name',
	'phonenumber',
	'date',
	'instructor',
	'signature',
];
interface Props {
	drivingSlipCase: GetMobileV2DrivingSlip_drivingSlip_case;
	caseNo: string;
	drivingSlipId: string;
}

const CaseOrganizationReport: React.FC<Props> = ({ drivingSlipCase, caseNo, drivingSlipId }): React.ReactElement => {
	const { t } = useTranslation();
	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);

	React.useEffect(() => {
		const formData = removeTypenameGeneric(drivingSlipCase.caseAsbestosReports?.caseOrganizationFormData?.caseOrganizationWorkers);
		if (formData !== null && formData !== undefined && formData.length !== 0) {
			setCaseOrganizationWorkers(formData as GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers[]);

			const fileNames = formData
				.filter(
					worker =>
						worker !== undefined &&
						worker !== null &&
						worker.signature !== undefined &&
						worker.signature !== null &&
						worker.signature.signatureDetails !== null &&
						worker.signature.signatureDetails.fileName,
				)
				.map(f => {
					return { fileName: f!.signature!.signatureDetails!.fileName, folder: undefined };
				});

			const existingFiles = checkFilesExist(fileNames, images);

			caseOrganizationWorkers.forEach((worker, index) => {
				if (existingFiles.some(ef => ef.fileName === worker.signature?.signatureDetails?.fileName)) {
					const file = tryGetFile(worker.signature!.signatureDetails!.fileName, images);

					setSignatureFiles(prevFiles => {
						const updatedFiles = [...(prevFiles || [])];
						updatedFiles[worker.index] = file;
						return updatedFiles;
					});
				} else {
					setSignatureFiles(prevFiles => {
						const updatedFiles = [...(prevFiles || [])];
						updatedFiles[worker.index] = null;
						return updatedFiles;
					});
				}
			});
		}
	}, [drivingSlipCase.caseAsbestosReports?.endevaluationReportFormData]);

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${drivingSlipCase.id}-case-organization-${name}`, initialValue);
	const [caseOrganizationWorkers, setCaseOrganizationWorkers] = useLocalStorage<GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers[]>(
		'caseOrganizationWorker-caseOrganization',
		[EMPTY_ROW(0)],
	);

	function addRow() {
		const lineNumber = caseOrganizationWorkers.length === 0 ? 0 : caseOrganizationWorkers[caseOrganizationWorkers.length - 1].index + 1;
		setCaseOrganizationWorkers(current => [...current, EMPTY_ROW(lineNumber)]);
		setActiveTab(caseOrganizationWorkers.length);
	}
	function removeRow(idx: number) {
		setCaseOrganizationWorkers(caseOrganizationWorkers.filter((_, i) => i !== idx));
		setSignatureFiles(prevFiles => {
			const updatedFiles = [...(prevFiles || [])];
			updatedFiles[idx] = null;
			return updatedFiles;
		});
	}
	const [removeWorkerData, setRemoveWorkerData] = React.useState<CaseOrganizationWorkerModalData | undefined>();
	const [presentRemoveWorkerModal, dismissRemoveWorkerModal] = useIonModal(RemoveWorkerModal, {
		worker: { index: removeWorkerData?.index, title: removeWorkerData?.title, name: removeWorkerData?.name },
		remove: removeRow,
		onDismiss: () => dismissRemoveWorkerModal(),
	} as RemoveWorkerModalProps);

	const [activeTab, setActiveTab] = React.useState<number>(0);

	const [signatureFiles, setSignatureFiles] = React.useState<(ESDHFileFragment | null)[]>();
	const [submittingImageWorkerIndex, setSubmittingImageWorkerIndex] = React.useState<number | undefined>();

	const [fileError, setFileError] = React.useState<boolean[]>([]);

	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const { upload, images } = useDrivingSlipImages(caseNo, drivingSlipId);

	const [createCaseOrganizationReport, { loading: submitting }] = useCreateCaseOrganizationReport();

	React.useEffect(() => {
		setSaved(false);
	}, [caseOrganizationWorkers]);

	const generateBaseOrganization = () => {
		if (caseOrganizationWorkers.length > 0) {
			return;
		}
		const newWorkers: GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers[] = [];
		if (drivingSlipCase.areaResponsible) {
			newWorkers.push({
				index: 0,
				title: t('reports.asbestos.areaResponsible'),
				name: drivingSlipCase.areaResponsible.name,
				instructor: '',
				phonenumber: drivingSlipCase.areaResponsible.phone ?? '',
				date: '',
				signature: null,
			});
		}
		if (drivingSlipCase.projectManager) {
			newWorkers.push({
				index: caseOrganizationWorkers.some(worker => worker.index === 0) ? 1 : 0,
				title: t('reports.asbestos.projectManager'),
				name: drivingSlipCase.projectManager.name ?? '',
				instructor: '',
				phonenumber: drivingSlipCase.projectManager.phone ?? '',
				date: '',
				signature: null,
			});
		}
		if (newWorkers.length > 0) {
			setCaseOrganizationWorkers(newWorkers);
		}
	};
	React.useEffect(() => {
		generateBaseOrganization();
	}, [drivingSlipCase]);

	const handleChange = (index: number, column: keyof GetMobileV2DrivingSlip_drivingSlip_case_caseAsbestosReports_caseOrganizationFormData_caseOrganizationWorkers, value: any) =>
		setCaseOrganizationWorkers(current =>
			current.map((worker, i) =>
				worker.index === index
					? {
							...worker,
							[column]: value,
					  }
					: worker,
			),
		);

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createCaseOrganizationReport({
				variables: {
					caseId: drivingSlipCase.id,
					formData: { caseOrganizationWorkers: formatWorkers(caseOrganizationWorkers) ?? [] },
					onlySave: true,
				},
				queueOfflineMutationOptions: {
					key: ApiMutationKey.DrivingSlip_CreateEndevaluationReport,
					removePreviousEntry: true,
					findPreviousEntryPredicate(variables) {
						return variables.caseId === drivingSlipCase.id;
					},
				},
			});
			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}

	return (
		<>
			<BasePage title={t('reports.reportTypes.caseOrganization')}>
				<IonGrid className="mb-4 text-sm">
					<IonRow className="text-lg text-blue-calm font-semibold">{t('reports.asbestos.workerList')}</IonRow>
					<ul className="mb-0 list-none flex-row flex-wrap pb-4 gap-2" role="tablist">
						{caseOrganizationWorkers.map((worker, idx) => {
							return (
								<li key={worker.index} className="-mb-px flex gap-2 pt-2 text-center last:mr-0">
									{idx === 0}
									<a
										className={
											'rounded-sm relative px-5 py-3 text-xs font-bold uppercase leading-normal shadow-xl w-full ' +
											(activeTab === idx ? 'bg-blue-calm text-white' : 'text-blue-calm bg-white')
										}
										onClick={e => {
											e.preventDefault();
											setActiveTab(idx);
										}}
										data-toggle="tab"
										href={'#link' + idx}
										role="tablist"
									>
										{`${worker.title || t('reports.asbestos.title')}  ${worker.name || t('reports.asbestos.name')}`}
									</a>
									<button
										style={{ right: '30px' }}
										className={'bg-transparent absolute right-8 focus:outline-none text-lg px-4 py-2 ' + (activeTab === idx ? 'text-white' : ' text-blue-calm ')}
										onClick={() => {
											setRemoveWorkerData({ index: idx, name: worker.name, title: worker.title });
											presentRemoveWorkerModal();
										}}
									>
										<IonIcon name="close" className="align-middle" slot="icon-only"></IonIcon>
									</button>
								</li>
							);
						})}
						{caseOrganizationWorkers.length === 0 && <li className="-mb-px mr-2 flex-auto text-center last:mr-0">{t('reports.asbestos.workerlistEmpty')}</li>}
						<li className="-mb-px mr-2 flex-auto text-center last:mr-0"></li>
					</ul>

					<Button className="w-full m-0 mt-4" onClick={() => addRow()}>
						{t('reports.asbestos.addWorker')}
					</Button>
					<IonRow className="text-lg text-blue-calm font-semibold mt-5">Medarbejder data</IonRow>
					{caseOrganizationWorkers.map((worker, index) => {
						return (
							<IonGrid key={index} className={activeTab === index ? 'block' : 'hidden'} id={'#link' + index}>
								<IonRow>
									{TABLE_COLUMNS.map((column, columnIndex) => {
										if (column === 'date') {
											return (
												<Datepicker
													key={columnIndex}
													title={`reports.asbestos.${column}`}
													name={column}
													value={worker[column] ?? ''}
													onChange={e => handleChange(worker.index, column, e.target.value)}
												/>
											);
										}
										if (column === 'signature') {
											return (
												<SignatureInput
													required={false}
													label={t(`reports.asbestos.${column}`)}
													isUploading={submittingImageWorkerIndex === worker.index}
													signatureFile={signatureFiles?.[worker.index] ?? undefined}
													onClear={() => {
														handleChange(worker.index, 'signature', undefined);
														setSignatureFiles(prevFiles => {
															const updatedFiles = [...(prevFiles || [])];
															updatedFiles[worker.index] = null;
															return updatedFiles;
														});
													}}
													onSubmit={async signature => {
														const fileAsBytes = stringToByteArray(signature.base64);
														const filename = `signature-${Date.now()}.${signature.extension}`;
														try {
															setSubmittingImageWorkerIndex(worker.index);
															setFileError([]);

															const file = await upload({
																filename: filename,
																fileData: fileAsBytes,
																blob: signature.base64,
															});
															handleChange(worker.index, 'signature', { signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName: filename } });

															setSignatureFiles(prevFiles => {
																const updatedFiles = [...(prevFiles || [])];
																updatedFiles[worker.index] = file;
																return updatedFiles;
															});
														} catch (error) {
															console.error(error);
															setFileError(prevErrors => {
																const updatedErrors = [...(prevErrors || [])];
																updatedErrors[worker.index] = true;
																return updatedErrors;
															});
														} finally {
															setSubmittingImageWorkerIndex(undefined);
														}
													}}
													errorMessage={fileError[worker.index] ? t('common.couldNotUpload') : undefined}
												/>
											);
										}
										return (
											<Input
												key={columnIndex}
												name={column}
												title={`reports.asbestos.${column}`}
												value={worker[column] ?? ''}
												onChange={e => handleChange(worker.index, column, e.target.value)}
											/>
										);
									})}
								</IonRow>
							</IonGrid>
						);
					})}

					<IonRow>
						<IonCol size="12">
							{saveButtonReportsFlag && (
								<Button expand="block" disabled={submitting || submittingImageWorkerIndex !== undefined} loading={submitting && loadingType === 'SAVE'} onClick={() => saveReport()}>
									{t(saved ? 'reports.saved' : 'reports.saveReport')}
								</Button>
							)}
						</IonCol>
					</IonRow>
				</IonGrid>
			</BasePage>
		</>
	);
};

export default CaseOrganizationReport;
function formatWorkers(workers: CaseOrganizationWorkerInput[]) {
	return workers.map(worker => ({
		...worker,
		date: worker.date === '' ? null : worker.date, // Set empty dates to null
	}));
}
